import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { InputChangeEvent } from 'fe-design-base/baseTypes';
import IconButton from 'fe-design-base/molecules/IconButton';

import { getCurrentCompanyHasMultipleLocations } from 'selectors/session';

import PurchaseMethodButton from 'features/biller/implementations/stripe/PurchaseMethodModule/PurchaseMethodButton';
import { Price } from 'features/biller/implementations/stripe/PurchaseMethodModule/PurchaseMethodModuleView';
import {
  useApplyDiscount,
  useAutoApplyDiscount,
} from 'features/productGrowth/components/CheckoutDrawer/hooks';
import { getCheckout } from 'features/productGrowth/components/CheckoutDrawer/selectors';
import {
  checkoutDismissed,
  checkoutSuccess,
  resetCheckout,
} from 'features/productGrowth/components/CheckoutDrawer/slice';
import {
  Checkout,
  Success,
} from 'features/productGrowth/components/CheckoutDrawer/views';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import HiringTieredOptions from './HiringTieredOptions';

const HIRING_ASSISTANT_TIERS: Record<any, any> = {
  UNLIMITED: {
    title: toI18n('checkout_drawer.hiring_assistant.growth'),
    subtitle: toI18n('checkout_drawer.hiring_assistant.growth_subheader'),
    pillText: toI18n('checkout_drawer.hiring_assistant.annual_discount'),
    priceInCents: 19900,
    perks: [
      {
        primary: toI18n('checkout_drawer.hiring_assistant.growth_perk_1'),
      },
      {
        primary: toI18n('checkout_drawer.hiring_assistant.growth_perk_2'),
      },
      {
        primary: toI18n('checkout_drawer.hiring_assistant.growth_perk_3'),
      },
      {
        primary: toI18n('checkout_drawer.hiring_assistant.growth_perk_4'),
      },
    ],
  },
  STARTER: {
    title: toI18n('checkout_drawer.hiring_assistant.starter'),
    subtitle: toI18n('checkout_drawer.hiring_assistant.starter_subheader'),
    priceInCents: 3000,
    perks: [
      {
        primary: toI18n('checkout_drawer.hiring_assistant.starter_perk_1'),
      },
      {
        primary: toI18n('checkout_drawer.hiring_assistant.starter_perk_2'),
      },
      {
        primary: toI18n('checkout_drawer.hiring_assistant.starter_perk_3'),
      },
    ],
  },
};

const { cx } = cxHelpers('CheckoutDrawer');

const HiringCheckoutDrawer = () => {
  const dispatch = useDispatch();
  const trackUxEvent = useTrackUx();
  const isMultiLocation = useSelector(getCurrentCompanyHasMultipleLocations);

  const [isLoading, setIsLoading] = useState(false);

  const [selectedTier, setSelectedTier] = useState('UNLIMITED');

  const { status, productInfo, isOpen, initiatedFrom, onSuccess } =
    useSelector(getCheckout);

  const [pillPromo, setPillPromo] = useState(
    initiatedFrom === 'HIRING_ASSISTANT_MIGRATION'
      ? 'checkout_drawer.hiring_assistant.three_months_free'
      : 'checkout_drawer.hiring_assistant.end_of_year_bonus'
  );

  const [checkoutStage, setCheckoutStage] = useState('tierChoice');

  const [selectedPeriod, setSelectedPeriod] = useState('annual');

  const defaultPrice: Price = {
    amountInCents: 0,
    priceSystemId: '',
  };

  const [selectedPrice, setSelectedPrice] = useState(defaultPrice);
  const [selectedProductUuid, setSelectedProductUuid] = useState<
    string | null | undefined
  >(null);

  const { manuallyApplyDiscount: manuallyApplyDiscountAnnual } =
    useApplyDiscount({
      discountCode:
        initiatedFrom === 'HIRING_ASSISTANT_MIGRATION'
          ? 'HIRINGEOY25'
          : 'HIRINGANNUAL',
      planName: 'hiring_assistant',
      billingCycle: 'monthly',
      productUuid: selectedProductUuid,
    });

  const { manuallyApplyDiscount: manuallyApplyDiscountMonthly } =
    useApplyDiscount({
      discountCode:
        initiatedFrom === 'HIRING_ASSISTANT_MIGRATION' ? 'HIRING3MO50' : null,
      planName: 'hiring_assistant',
      billingCycle: 'monthly',
      productUuid: selectedProductUuid,
    });

  const handleSetPrice = useCallback(
    (productName: string) => {
      const priceList = productInfo?.filter(
        (elem: any) => elem.product_name === productName
      );

      setSelectedPrice({
        amountInCents:
          priceList?.[0].price[0].amount_in_cents || defaultPrice.amountInCents,
        priceSystemId:
          priceList?.[0].price[0].system_id || defaultPrice.priceSystemId,
      });
      setSelectedProductUuid((priceList?.[0] as any)?.product_uuid || null);
    },
    [defaultPrice, productInfo]
  );

  useEffect(() => {
    const firstPrice = productInfo?.[0].price;

    if (!firstPrice) return;

    if (productInfo.length === 1) {
      setSelectedPrice({
        amountInCents: firstPrice[0].amount_in_cents,
        priceSystemId: firstPrice[0].system_id,
      });
      setSelectedProductUuid((productInfo[0] as any)?.product_uuid || null);
    } else {
      handleSetPrice('Hiring Assistant Unlimited Annual');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productInfo]);

  // Get Purchase Abandoned event data for Task Manager checkout
  const getPurchaseAbandonedEventData = useCallback(
    (closingWindow: boolean) => ({
      selected_plan: null, // No plan selection in add-on only checkout
      selected_billing_frequency: null, // No billing frequency for add-ons
      selected_add_on: ['hiring_assistant'],
      promo_code: null, // No promo code support in current implementation
      closing_window: closingWindow,
    }),
    []
  );

  const handleClose = useCallback(() => {
    if (status === 'success') {
      trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
        element: 'close_button',
        stage: 'checkout_completed',
      });
      dispatch(resetCheckout());
    } else {
      // Track Purchase Abandoned event
      trackUxEvent(
        'Purchase Abandoned',
        TRACK_ACTION_TYPES.CLICK,
        getPurchaseAbandonedEventData(false)
      );
      dispatch(checkoutDismissed());
    }
  }, [dispatch, status, trackUxEvent, getPurchaseAbandonedEventData]);

  const handleClickTier = useCallback(
    (tier: string) => {
      if (tier === 'UNLIMITED' && selectedPeriod === 'annual') {
        handleSetPrice('Hiring Assistant Unlimited Annual');
      } else if (tier === 'UNLIMITED' && selectedPeriod !== 'annual') {
        handleSetPrice('Hiring Assistant Unlimited');
      } else if (tier === 'STARTER') {
        handleSetPrice('Hiring Assistant Starter');
      }
      setSelectedTier(tier);
    },
    [handleSetPrice, selectedPeriod]
  );

  const handleSelectPlan = useCallback(() => {
    let selectedPlan = 'Hiring Assistant Unlimited Annual';

    if (selectedTier === 'UNLIMITED' && selectedPeriod !== 'annual') {
      selectedPlan = 'Hiring Unlimited Monthly';
    } else if (selectedTier === 'STARTER') {
      selectedPlan = 'Hiring Starter';
    }

    trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      button_text: 'Select plan',
      button_action: 'start_checkout',
      plan_selected: selectedPlan,
    });

    if (selectedTier === 'UNLIMITED' && selectedPeriod === 'annual') {
      manuallyApplyDiscountAnnual();
    }

    if (selectedTier === 'UNLIMITED' && selectedPeriod === 'monthly') {
      manuallyApplyDiscountMonthly();
    }

    setCheckoutStage('checkout');
  }, [
    manuallyApplyDiscountAnnual,
    manuallyApplyDiscountMonthly,
    selectedPeriod,
    selectedTier,
    trackUxEvent,
  ]);

  useAutoApplyDiscount({
    subscriptionType: 'hiring_assistant',
    productUuid: selectedProductUuid,
  });

  const handleSelectPeriod = useCallback(
    (event: InputChangeEvent) => {
      if (selectedTier === 'UNLIMITED') {
        if (event.target.value === 'annual') {
          handleSetPrice('Hiring Assistant Unlimited Annual');
        } else {
          handleSetPrice('Hiring Assistant Unlimited');
        }
      }

      if (initiatedFrom === 'HIRING_ASSISTANT_MIGRATION') {
        if (event.target.value === 'annual') {
          setPillPromo('checkout_drawer.hiring_assistant.three_months_free');
        } else {
          setPillPromo('checkout_drawer.hiring_assistant.50_percent_off');
        }
      }

      setSelectedPeriod(event.target.value);
    },
    [handleSetPrice, initiatedFrom, selectedTier]
  );

  // Add beforeunload event listener to detect window/tab closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track Purchase Abandoned event when closing window/tab
      if (status === 'success') {
        trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
          element: 'close_button',
          stage: 'checkout_completed',
        });
      } else {
        trackUxEvent(
          'Purchase Abandoned',
          TRACK_ACTION_TYPES.CLICK,
          getPurchaseAbandonedEventData(true)
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [trackUxEvent, getPurchaseAbandonedEventData, status]);

  const handleOnLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const handleSuccess = useCallback(async () => {
    setIsLoading(false);
    dispatch(checkoutSuccess());
    if (onSuccess) {
      await onSuccess();
    }
  }, [dispatch, onSuccess]);

  const renderDrawerContent = () => {
    // Prevents rendering the CheckoutDrawer component when initiatedFrom is set to null
    // when closing the drawer.
    if (!initiatedFrom) {
      return null;
    }

    const title = toI18n('checkout_drawer.hiring_assistant.header');
    const subtitle = toI18n('checkout_drawer.hiring_assistant.subheader');
    const tiers = HIRING_ASSISTANT_TIERS;

    if (status === 'success') {
      return <Success />;
    }

    if (tiers && checkoutStage === 'tierChoice') {
      return (
        <HiringTieredOptions
          tiers={isMultiLocation ? { UNLIMITED: tiers.UNLIMITED } : tiers}
          selectedTier={selectedTier}
          handleSelectPlan={handleSelectPlan}
          handleClickTier={handleClickTier}
          selectedPeriod={selectedPeriod}
          handleSelectPeriod={handleSelectPeriod}
          pillBelowAnnualPrice={
            initiatedFrom === 'HIRING_ASSISTANT_MIGRATION'
              ? toI18n(pillPromo)
              : null
          }
        />
      );
    }

    return (
      <Box mv={12} ph={32} flex column>
        <Box gap={12} column>
          <Text variant="heading2">{title}</Text>
          <Text variant="body" color="mono900">
            {subtitle}
          </Text>
        </Box>

        <Checkout
          price={selectedPrice}
          onSuccess={handleSuccess}
          onLoading={handleOnLoading}
          subscriptionType="hiring_assistant"
          selectedProductUuid={selectedProductUuid}
        />

        <PurchaseMethodButton fullWidth variant="primary" isLoading={isLoading}>
          {toI18n('checkout_drawer.confirm_and_pay')}
        </PurchaseMethodButton>
      </Box>
    );
  };

  return (
    <Drawer
      className={cx()}
      maxWidth="464px"
      open={isOpen}
      variant="temporary"
      onClose={handleClose}
    >
      <Box hright mt={16} mr={16}>
        <IconButton
          onClick={handleClose}
          icon="Close"
          size="small"
          uxElement="checkout_drawer.close_button"
          label={toI18n('checkout_drawer.close_button_labels.general')}
        />
      </Box>

      {renderDrawerContent()}
    </Drawer>
  );
};

const HiringCheckoutDrawerWithUxRoot = () => {
  const WrappedComponent = withUxRoot({
    productArea: PRODUCT_AREAS.HIRING_ASSISTANT,
    eventCategory: EVENT_CATEGORIES.CHECKOUT,
  })(HiringCheckoutDrawer);

  return <WrappedComponent />;
};

export default HiringCheckoutDrawerWithUxRoot;
